import React, { useState, useEffect } from 'react';
import { QrCode, Users, TrendingUp, Plus, MousePointerClick } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { StatCard } from '../StatCard';
import { Button } from '../Button';
import { QRCodeTable } from './QRCodeTable';
import { CreateQRCodeModal } from '../qr/CreateQRCodeModal';
import { qrApi } from '../../api/services/qr.service';
import { useAuth } from '../../contexts/AuthContext';
import { sampleQRCodeListEntry } from '../../data/sampleQrCode';

const quickActions = [
  {
    icon: Plus,
    title: 'Create New QR Code',
    description: 'Generate a new QR code',
    action: 'create' as const,
  },
  {
    icon: QrCode,
    title: 'View All QR Codes',
    description: 'Browse and manage all your QR codes',
    action: 'browse' as const,
  },
];

export function DashboardHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Treat undefined (stale cached user, pre-migration) as visible, same as the backend default.
  const showSampleDashboard = user?.show_sample_dashboard !== false;

  // Fetch QR codes for dashboard stats
  const { data: qrCodesResponse, isLoading: isLoadingQR } = useQuery({
    queryKey: ['qr-codes-dashboard'],
    queryFn: () => qrApi.getQRCodes({ page: 1, per_page: 100 }),
  });

  const qrCodes = qrCodesResponse?.items || [];
  const activeQRCodes = qrCodes.filter(qr => qr.is_active);

  // Calculate total scans across all QR codes
  const [totalScans, setTotalScans] = useState(0);
  const [uniqueUsers, setUniqueUsers] = useState(0);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (qrCodes.length === 0) {
        setIsLoadingStats(false);
        return;
      }

      try {
        // Fetch analytics for all QR codes
        const analyticsPromises = qrCodes.map(qr =>
          qrApi.getAnalyticsSummary(qr.short_code).catch(() => ({ total_scans: 0, unique_users: 0 }))
        );
        const analyticsResults = await Promise.all(analyticsPromises);

        // Sum up totals
        const scansTotal = analyticsResults.reduce((sum: number, result: any) => sum + (result.total_scans || 0), 0);
        const usersTotal = analyticsResults.reduce((sum: number, result: any) => sum + (result.unique_users || 0), 0);

        setTotalScans(scansTotal);
        setUniqueUsers(usersTotal);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchAnalytics();
  }, [qrCodes]);

  const scanRate = totalScans > 0 ? ((uniqueUsers / totalScans) * 100).toFixed(0) : '0';

  // Get recent QR codes (last 5), with the sample entry pinned to the top
  // while it's visible so a new user always has something to click into.
  const realRecentQRCodes = qrCodes
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, showSampleDashboard ? 4 : 5);
  const recentQRCodes = showSampleDashboard
    ? [sampleQRCodeListEntry, ...realRecentQRCodes]
    : realRecentQRCodes;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="heading-1 mb-2">Dashboard</h1>
          <p className="text-[--clinqr-text-secondary]">Welcome back!</p>
        </div>
        <div className="text-sm text-[--clinqr-text-muted] font-medium">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-6 mb-12">
        <StatCard
          value={isLoadingQR ? '...' : activeQRCodes.length.toString()}
          label="Active QR Codes"
          icon={QrCode}
          trend={qrCodes.length > 0 ? { value: `${activeQRCodes.length}/${qrCodes.length}`, isPositive: true } : undefined}
          className="bg-[--clinqr-bg-subtle]/30 border-[--clinqr-border]/5"
        />
        <StatCard
          value={isLoadingStats ? '...' : totalScans.toLocaleString()}
          label="Total Scans"
          icon={MousePointerClick}
          className="bg-[--clinqr-bg-subtle]/30 border-[--clinqr-border]/5"
        />
        <StatCard
          value={isLoadingStats ? '...' : uniqueUsers.toLocaleString()}
          label="Unique Visitors"
          icon={Users}
          className="bg-[--clinqr-bg-subtle]/30 border-[--clinqr-border]/5"
        />
        <StatCard
          value={isLoadingStats ? '...' : `${scanRate}%`}
          label="Unique Rate"
          icon={TrendingUp}
          className="bg-[--clinqr-bg-subtle]/30 border-[--clinqr-border]/5"
        />
      </div>

      {/* Quick Actions */}
      <div className="mb-12">
        <h2 className="heading-3 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-6">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <div
                key={index}
                onClick={() => (action.action === 'create' ? setIsCreateModalOpen(true) : navigate('/dashboard/qr-codes'))}
                className="bg-white rounded-xl p-6 border border-[--clinqr-border]/10 hover:border-[--clinqr-primary]/30 hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer group"
              >
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-[--clinqr-primary] flex items-center justify-center flex-shrink-0 transition-transform">
                    <Icon className="w-7 h-7 text-[--clinqr-bg]" strokeWidth={2} />
                  </div>
                  <div>
                    <h3 className="heading-5 mb-1">{action.title}</h3>
                    <p className="text-sm text-[--clinqr-text-secondary] leading-relaxed">
                      {action.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="heading-3">Recent QR Codes</h2>
          <button
            onClick={() => navigate('/dashboard/qr-codes')}
            className="text-sm text-[--clinqr-primary] hover:underline font-semibold tracking-tight"
          >
            View all
          </button>
        </div>

        {isLoadingQR ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[--clinqr-primary] mx-auto mb-4"></div>
            <p className="text-[--clinqr-text-secondary]">Loading QR codes...</p>
          </div>
        ) : recentQRCodes.length === 0 ? (
          <div className="bg-white rounded-xl border border-[--clinqr-border]/10 overflow-hidden shadow-sm p-12 text-center">
            <QrCode className="w-16 h-16 text-[--clinqr-text-muted] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[--clinqr-text-primary] mb-2">No QR codes yet</h3>
            <p className="text-[--clinqr-text-secondary] mb-6">Create your first QR code to get started</p>
            <Button onClick={() => setIsCreateModalOpen(true)} className="mx-auto">
              Create QR Code
            </Button>
          </div>
        ) : (
          <QRCodeTable qrCodes={recentQRCodes} compact />
        )}
      </div>

      <CreateQRCodeModal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
    </div>
  );
}
